// Expedition entry + completion modals (Phase 5 Step 17).
//
// Two PixiJS modals sharing one module because they're mutually
// exclusive in practice (you can't receive a completion envelope while
// a ready-prompt is open) and the rendering code is near-identical:
//   - ExpeditionReadyModal: Accept/Decline prompt on pendingReady.
//     ESC or click-outside = Decline (per spec "panel modal convention").
//   - ExpeditionCompletedModal: terminal "Return to Station" prompt on
//     completed state; click closes + sends exitExpedition.
//
// Modal lifecycle via LocalController.setModal to keep movement keys
// suppressed — matches workbench/market/party panel convention. The
// orchestrator exposes an `isOpen` getter that the input-router consults
// so the ESC binding closes the right layer.

import { Application, Container, Graphics, Text } from "pixi.js";
import type { ClientMessage } from "../../shared/messages.ts";
import {
  buildAcceptExpeditionReadyMessage,
  buildDeclineExpeditionReadyMessage,
  buildExitExpeditionMessage,
} from "../../shared/expedition-messages.ts";
import {
  DISMISSED_INVITES_STORAGE_KEY,
  scheduleAcceptanceFallback,
  type ExpeditionClientState,
  type ExpeditionCompletedEntry,
  type PendingReady,
} from "./expedition-state.ts";

// Phase 7 Block A — playtest finding 8.5. The ZoneDO ephemeral push
// of `expeditionReadyConfirmed` races socket close during the cross-DO
// transition; if it loses, the client sits with `acceptedPending` set
// and never reconnects to ExpeditionDO. We wait this long after
// Accept before forcing the reconnect ourselves.
const ACCEPT_FALLBACK_DELAY_MS = 3000; // contracts:allow-literal — fallback grace

// ---- layout constants ----
const PANEL_W = 420; // contracts:allow-literal — entry-modal width
const PANEL_H = 220; // contracts:allow-literal — entry-modal height
const PAD = 16; // contracts:allow-literal — inner padding
const BTN_W = 96; // contracts:allow-literal — button width
const BTN_H = 28; // contracts:allow-literal — button height
const PANEL_BG = 0x12122a;
const PANEL_BORDER = 0x4455cc;
const BTN_BG = 0x2a2a55;
const BTN_BG_HOVER = 0x3a3a66;
const TEXT_COLOR = 0xeeeeff;
const MUTED_COLOR = 0x8888aa;

// Full-screen dim backdrop behind the modal — click-outside = decline.
const SCRIM_ALPHA = 0.55; // contracts:allow-literal — modal scrim alpha

export type ExpeditionReadyUIDeps = {
  app: Application;
  send: (msg: ClientMessage) => void;
  onOpen: () => void;
  onClose: () => void;
  // Callback for Accept: after send, the caller records the pending
  // accept id in state so the expeditionEntered envelope can trigger
  // the WS reconnect. Keeps this module decoupled from the reducer.
  onAccept: (expeditionId: string) => void;
  // Callback for Decline: the caller persists the dismissedInvites set.
  onDecline: (expeditionId: string) => void;
  // Return to station click: caller clears completed state.
  onReturnToStation: () => void;
};

export class ExpeditionReadyUI {
  private readonly root = new Container();
  private readonly scrim: Graphics;
  private readonly panel = new Container();
  private readonly body = new Container();
  private currentKind: "ready" | "completed" | null = null;
  private currentReady: PendingReady | null = null;

  constructor(private readonly deps: ExpeditionReadyUIDeps) {
    this.root.visible = false;
    this.root.zIndex = 130; // contracts:allow-literal — above party panel (120)
    deps.app.stage.sortableChildren = true;
    deps.app.stage.addChild(this.root);

    this.scrim = new Graphics();
    this.scrim.eventMode = "static";
    this.scrim.cursor = "default";
    this.root.addChild(this.scrim);

    const bg = new Graphics()
      .rect(0, 0, PANEL_W, PANEL_H)
      .fill({ color: PANEL_BG })
      .stroke({ color: PANEL_BORDER, width: 2 }); // contracts:allow-literal — border width
    this.panel.addChild(bg);
    this.panel.eventMode = "static";
    this.root.addChild(this.panel);

    this.panel.addChild(this.body);
    this.body.x = PAD;
    this.body.y = PAD;

    this.layout();
    deps.app.renderer.on("resize", () => this.layout());
  }

  get isOpen(): boolean {
    return this.currentKind !== null;
  }

  // Called by main.ts each frame so a pendingReady appearing mid-tick
  // opens without a dedicated event hook. Cheap idempotent sync.
  sync(pendingReady: PendingReady | null, completed: ExpeditionCompletedEntry | null): void {
    // Completed has priority over pendingReady — the expedition is done,
    // a stale invite for a different exp shouldn't block the return cue.
    if (completed !== null) {
      if (this.currentKind !== "completed") this.openCompleted(completed);
      else this.renderCompleted(completed);
      return;
    }
    if (pendingReady !== null) {
      if (this.currentKind !== "ready" || this.currentReady?.expeditionId !== pendingReady.expeditionId) {
        this.openReady(pendingReady);
      } else {
        this.renderReady(pendingReady);
      }
      return;
    }
    if (this.currentKind !== null) this.close();
  }

  // Called from input-router when ESC is pressed and this modal owns
  // the top layer. ESC semantics per spec:
  //   - Ready modal → decline
  //   - Completed modal → no-op (terminal; user must click Return)
  handleEscape(): void {
    if (this.currentKind === "ready" && this.currentReady !== null) {
      this.declineCurrent();
    }
  }

  private openReady(pending: PendingReady): void {
    this.currentKind = "ready";
    this.currentReady = pending;
    this.root.visible = true;
    this.deps.onOpen();
    this.renderReady(pending);
  }

  private openCompleted(entry: ExpeditionCompletedEntry): void {
    this.currentKind = "completed";
    this.currentReady = null;
    this.root.visible = true;
    this.deps.onOpen();
    this.renderCompleted(entry);
  }

  private close(): void {
    if (this.currentKind === null) return;
    this.currentKind = null;
    this.currentReady = null;
    this.root.visible = false;
    this.deps.onClose();
  }

  private renderReady(pending: PendingReady): void {
    for (const child of this.body.removeChildren()) child.destroy();

    const title = new Text({
      text: `${pending.leaderName} invites you`,
      style: { fontSize: 18, fill: TEXT_COLOR, fontFamily: "monospace", fontWeight: "bold" }, // contracts:allow-literal — title font
    });
    title.y = 0;
    this.body.addChild(title);

    const archetypeLabel = new Text({
      text: `Expedition: ${pending.archetype}`,
      style: { fontSize: 14, fill: TEXT_COLOR, fontFamily: "monospace" }, // contracts:allow-literal — subtitle font
    });
    archetypeLabel.y = 32; // contracts:allow-literal — below title
    this.body.addChild(archetypeLabel);

    const flavor = new Text({
      text: "Derelict beacon locked. Board with your crew?",
      style: { fontSize: 12, fill: MUTED_COLOR, fontFamily: "monospace" }, // contracts:allow-literal — flavor font
    });
    flavor.y = 64; // contracts:allow-literal — below subtitle
    this.body.addChild(flavor);

    // Accept button (right).
    const accept = buildButton("Accept", () => this.acceptCurrent());
    accept.x = PANEL_W - PAD * 2 - BTN_W; // contracts:allow-literal — right-align
    accept.y = PANEL_H - PAD * 2 - BTN_H; // contracts:allow-literal — bottom-align
    this.body.addChild(accept);

    // Decline button (left of accept).
    const decline = buildButton("Decline", () => this.declineCurrent());
    decline.x = PANEL_W - PAD * 2 - BTN_W * 2 - 12; // contracts:allow-literal — gap between buttons
    decline.y = PANEL_H - PAD * 2 - BTN_H; // contracts:allow-literal — bottom-align
    this.body.addChild(decline);

    this.scrim.eventMode = "static";
    this.scrim.removeAllListeners();
    this.scrim.on("pointerdown", () => this.declineCurrent());
  }

  private renderCompleted(entry: ExpeditionCompletedEntry): void {
    for (const child of this.body.removeChildren()) child.destroy();

    const title = new Text({
      text: "Expedition Complete",
      style: { fontSize: 18, fill: TEXT_COLOR, fontFamily: "monospace", fontWeight: "bold" }, // contracts:allow-literal — title font
    });
    title.y = 0;
    this.body.addChild(title);

    const reasonText = entry.reason === "boss_defeated" ? "Boss Defeated" : "Abandoned";
    const subtitle = new Text({
      text: reasonText,
      style: { fontSize: 14, fill: TEXT_COLOR, fontFamily: "monospace" }, // contracts:allow-literal — subtitle font
    });
    subtitle.y = 32; // contracts:allow-literal — below title
    this.body.addChild(subtitle);

    const returnBtn = buildButton("Return to Station", () => this.returnToStation());
    returnBtn.x = PANEL_W - PAD * 2 - BTN_W * 2; // contracts:allow-literal — widened button slot
    returnBtn.y = PANEL_H - PAD * 2 - BTN_H; // contracts:allow-literal — bottom-align
    this.body.addChild(returnBtn);

    // Scrim is inert on the completed modal — forcing the user to
    // click Return keeps the exit flow deliberate.
    this.scrim.eventMode = "none";
    this.scrim.removeAllListeners();
  }

  private acceptCurrent(): void {
    if (this.currentKind !== "ready" || this.currentReady === null) return;
    const expeditionId = this.currentReady.expeditionId;
    this.deps.send(buildAcceptExpeditionReadyMessage(expeditionId));
    this.deps.onAccept(expeditionId);
    this.close();
  }

  private declineCurrent(): void {
    if (this.currentKind !== "ready" || this.currentReady === null) return;
    const expeditionId = this.currentReady.expeditionId;
    this.deps.send(buildDeclineExpeditionReadyMessage(expeditionId));
    this.deps.onDecline(expeditionId);
    this.close();
  }

  private returnToStation(): void {
    if (this.currentKind !== "completed") return;
    this.deps.send(buildExitExpeditionMessage());
    this.deps.onReturnToStation();
    this.close();
  }

  private layout(): void {
    const screenW = this.deps.app.renderer.width;
    const screenH = this.deps.app.renderer.height;
    this.scrim.clear();
    this.scrim.rect(0, 0, screenW, screenH).fill({ color: 0x000000, alpha: SCRIM_ALPHA });
    this.panel.x = (screenW - PANEL_W) / 2; // contracts:allow-literal — centering idiom
    this.panel.y = (screenH - PANEL_H) / 2; // contracts:allow-literal — centering idiom
  }
}

// Factory — keeps main.ts clean of the onAccept/onDecline/onReturn
// bookkeeping (localStorage persistence + state writes). Consumer
// receives a ready-to-go ExpeditionReadyUI + the callback that the
// reducer fires on expeditionEntered to trigger WS reconnect.
export type AttachExpeditionReadyDeps = {
  app: Application;
  send: (msg: ClientMessage) => void;
  setModal: (open: boolean) => void;
  expedition: ExpeditionClientState;
  storage: { getItem(k: string): string | null; setItem(k: string, v: string): void };
  // Phase 7 Block A — invoked when the ZoneDO ephemeral
  // expeditionReadyConfirmed race lost and the fallback timer elapsed.
  // Caller should clear resume state + reconnect so the Worker reroutes
  // to ExpeditionDO via the now-flipped current_expedition_id column.
  forceReconnectAfterAccept: () => void;
};
export function attachExpeditionReadyUI(deps: AttachExpeditionReadyDeps): ExpeditionReadyUI {
  return new ExpeditionReadyUI({
    app: deps.app,
    send: deps.send,
    onOpen: () => deps.setModal(true),
    onClose: () => deps.setModal(false),
    onAccept: (expeditionId) => {
      deps.expedition.acceptedPending = expeditionId;
      deps.expedition.pendingReady = null;
      // Fallback (8.5): if no reconnect signal lands within
      // ACCEPT_FALLBACK_DELAY_MS, force the reconnect ourselves.
      scheduleAcceptanceFallback({
        expeditionId,
        state: deps.expedition,
        forceReconnect: deps.forceReconnectAfterAccept,
        setTimer: (cb, ms) => window.setTimeout(cb, ms),
        delayMs: ACCEPT_FALLBACK_DELAY_MS,
      });
    },
    onDecline: (expeditionId) => {
      deps.expedition.dismissedInvites.add(expeditionId);
      deps.storage.setItem(DISMISSED_INVITES_STORAGE_KEY, JSON.stringify([...deps.expedition.dismissedInvites]));
      deps.expedition.pendingReady = null;
    },
    onReturnToStation: () => { deps.expedition.completed = null; },
  });
}

function buildButton(label: string, onClick: () => void): Container {
  const root = new Container();
  root.eventMode = "static";
  root.cursor = "pointer";
  const bg = new Graphics()
    .rect(0, 0, BTN_W, BTN_H)
    .fill({ color: BTN_BG });
  root.addChild(bg);
  const t = new Text({
    text: label,
    style: { fontSize: 13, fill: TEXT_COLOR, fontFamily: "monospace", fontWeight: "bold" }, // contracts:allow-literal — button font
  });
  t.x = (BTN_W - t.width) / 2; // contracts:allow-literal — centering idiom
  t.y = (BTN_H - t.height) / 2; // contracts:allow-literal — centering idiom
  root.addChild(t);
  root.on("pointerover", () => { bg.tint = BTN_BG_HOVER; });
  root.on("pointerout", () => { bg.tint = 0xffffff; });
  root.on("pointerdown", onClick);
  return root;
}
